import { useCallback, useEffect } from "react";

interface LightboxProps {
  images: { id: number; image: string; title: string; subtitle: string; category: string }[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export default function Lightbox({ images, currentIndex, onClose, onPrev, onNext }: LightboxProps) {
  const current = images[currentIndex];

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    },
    [onClose, onPrev, onNext]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [handleKeyDown]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 animate-opacity-in"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 md:top-8 md:right-8 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors z-10 cursor-pointer"
        aria-label="Close lightbox"
      >
        <i className="ri-close-line text-xl md:text-2xl"></i>
      </button>

      <div className="absolute top-5 left-5 md:top-8 md:left-8 z-10">
        <span className="text-background-50/80 text-sm md:text-base font-medium tracking-wide">
          {currentIndex + 1} / {images.length}
        </span>
      </div>

      <div className="absolute top-5 right-16 md:top-8 md:right-20 z-10">
        <span className="bg-accent-500 text-background-50 text-xs md:text-sm font-medium px-3 py-1 rounded-full">
          {current.category}
        </span>
      </div>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors z-10 cursor-pointer"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors z-10 cursor-pointer"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
          </button>
        </>
      )}

      <div
        className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={current.image}
          alt={current.title}
          className="max-w-full max-h-[90vh] object-contain rounded-lg animate-scale-in"
        />
      </div>
    </div>
  );
}